
import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';

interface Author {
  id: string;
  name: string;
  slug: string;
  avatar_url: string | null;
  bio: string | null;
  role: string;
  website_url: string | null;
  twitter_url: string | null;
  is_active: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export default function AuthorsPage() {
  const [authors, setAuthors] = useState<Author[]>([]);
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editingAuthor, setEditingAuthor] = useState<Author | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [postCounts, setPostCounts] = useState<Record<string, number>>({});
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    avatar_url: '',
    bio: '',
    role: '',
    website_url: '',
    twitter_url: '',
    is_active: true,
  });

  // -------------------------------------------------------------------------
  // Data fetching
  // -------------------------------------------------------------------------
  useEffect(() => {
    fetchAuthors();
    fetchPostCounts();
  }, []);

  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => setNotification(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  const fetchAuthors = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('authors')
      .select('*')
      .order('sort_order', { ascending: true });

    if (error) {
      setNotification({ type: 'error', message: '著者の取得に失敗しました' });
    } else {
      setAuthors(data ?? []);
    }
    setLoading(false);
  };

  const fetchPostCounts = async () => {
    const { data, error } = await supabase.from('blog_posts').select('author');
    if (!error && data) {
      const counts: Record<string, number> = {};
      data.forEach((post: any) => {
        const author = post.author || '';
        counts[author] = (counts[author] || 0) + 1;
      });
      setPostCounts(counts);
    }
  };

  // -------------------------------------------------------------------------
  // Helpers
  // -------------------------------------------------------------------------
  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .replace(/[^\w\s\u3040-\u309f\u30a0-\u30ff\u4e00-\u9faf-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  };

  // -------------------------------------------------------------------------
  // Modal handling
  // -------------------------------------------------------------------------
  const openCreateModal = () => {
    setEditingAuthor(null);
    setFormData({
      name: '',
      slug: '',
      avatar_url: '',
      bio: '',
      role: '',
      website_url: '',
      twitter_url: '',
      is_active: true,
    });
    setShowModal(true);
  };

  const openEditModal = (author: Author) => {
    setEditingAuthor(author);
    setFormData({
      name: author.name,
      slug: author.slug,
      avatar_url: author.avatar_url ?? '',
      bio: author.bio ?? '',
      role: author.role ?? '',
      website_url: author.website_url ?? '',
      twitter_url: author.twitter_url ?? '',
      is_active: author.is_active,
    });
    setShowModal(true);
  };

  // -------------------------------------------------------------------------
  // Avatar upload
  // -------------------------------------------------------------------------
  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (file.size > 5 * 1024 * 1024) {
      setNotification({ type: 'error', message: '画像サイズは5MB以下にしてください' });
      return;
    }
    if (!allowedTypes.includes(file.type)) {
      setNotification({ type: 'error', message: 'JPEG、PNG、GIF、WebP形式のみ対応しています' });
      return;
    }

    setUploading(true);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(2, 9)}.${fileExt}`;
      const filePath = `authors/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('blog-images')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data } = supabase.storage.from('blog-images').getPublicUrl(filePath);
      const publicUrl = data?.publicUrl ?? '';

      setFormData(prev => ({ ...prev, avatar_url: publicUrl }));
      setNotification({ type: 'success', message: 'アバター画像をアップロードしました' });
    } catch (error) {
      console.error('Upload error:', error);
      setNotification({ type: 'error', message: '画像のアップロードに失敗しました' });
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // -------------------------------------------------------------------------
  // Save (create / update)
  // -------------------------------------------------------------------------
  const handleSave = async () => {
    if (!formData.name.trim()) {
      setNotification({ type: 'error', message: '著者名を入力してください' });
      return;
    }
    if (!formData.slug.trim()) {
      setNotification({ type: 'error', message: 'スラッグを入力してください' });
      return;
    }

    setSaving(true);

    const payload = {
      name: formData.name.trim(),
      slug: formData.slug.trim(),
      avatar_url: formData.avatar_url.trim() || null,
      bio: formData.bio.trim() || null,
      role: formData.role.trim(),
      website_url: formData.website_url.trim() || null,
      twitter_url: formData.twitter_url.trim() || null,
      is_active: formData.is_active,
      updated_at: new Date().toISOString(),
    };

    if (editingAuthor) {
      // Update existing author
      const { error } = await supabase
        .from('authors')
        .update(payload)
        .eq('id', editingAuthor.id);

      if (error) {
        setNotification({
          type: 'error',
          message: error.message.includes('unique')
            ? 'このスラッグは既に使用されています'
            : '著者の更新に失敗しました',
        });
      } else {
        // Propagate name/avatar changes to blog_posts
        if (editingAuthor.name !== payload.name) {
          await supabase
            .from('blog_posts')
            .update({ author: payload.name })
            .eq('author', editingAuthor.name);
        }
        if (editingAuthor.avatar_url !== payload.avatar_url) {
          await supabase
            .from('blog_posts')
            .update({ author_avatar: payload.avatar_url })
            .eq('author', payload.name);
        }

        setNotification({ type: 'success', message: '著者を更新しました' });
        setShowModal(false);
        await fetchAuthors();
        await fetchPostCounts();
      }
    } else {
      // Create new author
      const maxOrder =
        authors.length > 0
          ? Math.max(...authors.map(a => a.sort_order)) + 1
          : 0;

      const { error } = await supabase
        .from('authors')
        .insert({ ...payload, sort_order: maxOrder });

      if (error) {
        setNotification({
          type: 'error',
          message: error.message.includes('unique')
            ? 'このスラッグは既に使用されています'
            : '著者の作成に失敗しました',
        });
      } else {
        setNotification({ type: 'success', message: '著者を作成しました' });
        setShowModal(false);
        await fetchAuthors();
      }
    }

    setSaving(false);
  };

  // -------------------------------------------------------------------------
  // Toggle active status
  // -------------------------------------------------------------------------
  const handleToggle = async (author: Author) => {
    const { error } = await supabase
      .from('authors')
      .update({
        is_active: !author.is_active,
        updated_at: new Date().toISOString(),
      })
      .eq('id', author.id);

    if (error) {
      setNotification({ type: 'error', message: 'ステータスの変更に失敗しました' });
    } else {
      setNotification({
        type: 'success',
        message: author.is_active ? '著者を無効化しました' : '著者を有効化しました',
      });
      await fetchAuthors();
    }
  };

  // -------------------------------------------------------------------------
  // Delete author
  // -------------------------------------------------------------------------
  const handleDelete = async (id: string) => {
    const author = authors.find(a => a.id === id);
    if (author && (postCounts[author.name] ?? 0) > 0) {
      setNotification({
        type: 'error',
        message: `この著者には${postCounts[author.name]}件の記事があります。先に記事の著者を変更してください。`,
      });
      setDeleteConfirm(null);
      return;
    }

    const { error } = await supabase.from('authors').delete().eq('id', id);
    if (error) {
      setNotification({ type: 'error', message: '著者の削除に失敗しました' });
    } else {
      setNotification({ type: 'success', message: '著者を削除しました' });
      await fetchAuthors();
    }
    setDeleteConfirm(null);
  };

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link
              to="/admin"
              className="text-gray-500 hover:text-gray-700 transition-colors cursor-pointer"
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-arrow-left-line text-xl"></i>
              </div>
            </Link>
            <h1 className="text-xl font-bold text-gray-900">著者設定</h1>
          </div>
          <button
            onClick={openCreateModal}
            className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors flex items-center gap-2 whitespace-nowrap cursor-pointer"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-add-line"></i>
            </div>
            新規著者
          </button>
        </div>
      </header>

      {/* Notification */}
      {notification && (
        <div
          className={`fixed top-4 right-4 z-50 px-6 py-4 rounded-lg shadow-xl max-w-md ${
            notification.type === 'success' ? 'bg-teal-600 text-white' : 'bg-red-500 text-white'
          }`}
        >
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <i
                className={`${
                  notification.type === 'success' ? 'ri-checkbox-circle-fill' : 'ri-error-warning-fill'
                } text-xl`}
              ></i>
              <span className="font-semibold">{notification.message}</span>
            </div>
            <button
              onClick={() => setNotification(null)}
              className="text-white hover:text-gray-200 cursor-pointer"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>
        </div>
      )}

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Info */}
        <div className="bg-teal-50 border border-teal-200 rounded-lg p-4 mb-6 flex items-start gap-3">
          <div className="w-5 h-5 flex items-center justify-center text-teal-600 flex-shrink-0 mt-0.5">
            <i className="ri-information-line text-lg"></i>
          </div>
          <p className="text-sm text-teal-800">
            著者の追加・編集ができます。記事が紐づいている著者は削除できません。著者名を変更すると、紐づいている記事の著者名も自動で更新されます。
          </p>
        </div>

        {/* Author Cards */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <i className="ri-loader-4-line animate-spin text-2xl text-gray-400 mr-2"></i>
            <span className="text-gray-500">読み込み中...</span>
          </div>
        ) : authors.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 text-gray-300">
              <i className="ri-user-line text-5xl"></i>
            </div>
            <p className="text-gray-500 mb-4">著者がまだ登録されていません</p>
            <button
              onClick={openCreateModal}
              className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              最初の著者を登録
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {authors.map(author => (
              <div
                key={author.id}
                className={`bg-white rounded-xl border shadow-sm overflow-hidden transition-all hover:shadow-md ${
                  author.is_active ? 'border-gray-200' : 'border-gray-200 opacity-60'
                }`}
              >
                <div className="p-5">
                  <div className="flex items-start gap-4">
                    {/* Avatar */}
                    <div className="w-16 h-16 rounded-full bg-gray-100 flex-shrink-0 overflow-hidden flex items-center justify-center">
                      {author.avatar_url ? (
                        <img
                          src={author.avatar_url}
                          alt={author.name}
                          className="w-16 h-16 rounded-full object-cover"
                        />
                      ) : (
                        <i className="ri-user-line text-2xl text-gray-400"></i>
                      )}
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-gray-900 truncate">{author.name}</h3>
                        <span
                          className={`inline-flex items-center px-2 py-0.5 text-xs rounded-full ${
                            author.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                          }`}
                        >
                          {author.is_active ? '有効' : '無効'}
                        </span>
                      </div>
                      {author.role && <p className="text-sm text-teal-600 mb-1">{author.role}</p>}
                      <p className="text-xs text-gray-400 mb-2">/{author.slug}</p>
                      {author.bio && (
                        <p className="text-sm text-gray-600 line-clamp-2">{author.bio}</p>
                      )}
                    </div>
                  </div>

                  {/* Links */}
                  <div className="flex items-center gap-3 mt-4">
                    {author.website_url && (
                      <a
                        href={author.website_url}
                        target="_blank"
                        rel="nofollow noopener noreferrer"
                        className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-teal-600 transition-colors cursor-pointer"
                      >
                        <i className="ri-global-line text-lg"></i>
                      </a>
                    )}
                    {author.twitter_url && (
                      <a
                        href={author.twitter_url}
                        target="_blank"
                        rel="nofollow noopener noreferrer"
                        className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-teal-600 transition-colors cursor-pointer"
                      >
                        <i className="ri-twitter-x-line text-lg"></i>
                      </a>
                    )}
                    <div className="ml-auto flex items-center gap-1 text-sm text-gray-500">
                      <i className="ri-article-line"></i>
                      <span>{postCounts[author.name] || 0}件</span>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="border-t border-gray-100 px-5 py-3 flex items-center gap-3 bg-gray-50/50">
                  <button
                    onClick={() => openEditModal(author)}
                    className="text-teal-600 hover:text-teal-800 text-sm cursor-pointer whitespace-nowrap"
                  >
                    編集
                  </button>
                  <button
                    onClick={() => handleToggle(author)}
                    className="text-amber-600 hover:text-amber-800 text-sm cursor-pointer whitespace-nowrap"
                  >
                    {author.is_active ? '無効化' : '有効化'}
                  </button>
                  <button
                    onClick={() => setDeleteConfirm(author.id)}
                    className="text-red-500 hover:text-red-700 text-sm cursor-pointer whitespace-nowrap"
                  >
                    削除
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-40 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
              <h2 className="text-lg font-bold text-gray-900">
                {editingAuthor ? '著者編集' : '新規著者'}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-500 hover:text-gray-700 cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            <div className="p-6 space-y-5">
              {/* Avatar Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  アバター画像
                </label>
                <div className="flex items-center gap-4">
                  <div className="w-20 h-20 rounded-full bg-gray-100 overflow-hidden flex items-center justify-center flex-shrink-0">
                    {formData.avatar_url ? (
                      <img
                        src={formData.avatar_url}
                        alt="アバター"
                        className="w-20 h-20 rounded-full object-cover"
                      />
                    ) : (
                      <i className="ri-user-line text-3xl text-gray-400"></i>
                    )}
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="px-3 py-1.5 bg-white border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer inline-flex items-center gap-1.5">
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleAvatarUpload}
                        disabled={uploading}
                        className="hidden"
                      />
                      {uploading ? (
                        <>
                          <i className="ri-loader-4-line animate-spin"></i>
                          アップロード中...
                        </>
                      ) : (
                        <>
                          <i className="ri-upload-2-line"></i>
                          画像を選択
                        </>
                      )}
                    </label>
                    {formData.avatar_url && (
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, avatar_url: '' })}
                        className="text-xs text-red-500 hover:text-red-700 cursor-pointer text-left"
                      >
                        画像を削除
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  著者名 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-teal-500 focus:ring-1 focus:ring-teal-500 outline-none"
                  value={formData.name}
                  onChange={e =>
                    setFormData({
                      ...formData,
                      name: e.target.value,
                      slug: editingAuthor ? formData.slug : generateSlug(e.target.value),
                    })
                  }
                  placeholder="例：配信技術部 高橋"
                />
              </div>

              {/* Slug */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  スラッグ <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-teal-500 focus:ring-1 focus:ring-teal-500 outline-none"
                  value={formData.slug}
                  onChange={e => setFormData({ ...formData, slug: e.target.value })}
                  placeholder="例：takahashi"
                />
                <p className="mt-1 text-xs text-gray-400">URLに使用される識別子です</p>
              </div>

              {/* Role */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  役職・肩書き
                </label>
                <input
                  type="text"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-teal-500 focus:ring-1 focus:ring-teal-500 outline-none"
                  value={formData.role}
                  onChange={e => setFormData({ ...formData, role: e.target.value })}
                  placeholder="例：配信技術部 リーダー"
                />
              </div>

              {/* Bio */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  自己紹介
                </label>
                <textarea
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-teal-500 focus:ring-1 focus:ring-teal-500 outline-none"
                  rows={3}
                  value={formData.bio}
                  onChange={e => setFormData({ ...formData, bio: e.target.value })}
                  placeholder="著者の自己紹介文（任意）"
                  maxLength={500}
                />
              </div>

              {/* Website URL */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  ウェブサイトURL
                </label>
                <input
                  type="url"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-teal-500 focus:ring-1 focus:ring-teal-500 outline-none"
                  value={formData.website_url}
                  onChange={e => setFormData({ ...formData, website_url: e.target.value })}
                  placeholder="https://example.com"
                />
              </div>

              {/* Twitter URL */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  X（Twitter）URL
                </label>
                <input
                  type="url"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-teal-500 focus:ring-1 focus:ring-teal-500 outline-none"
                  value={formData.twitter_url}
                  onChange={e => setFormData({ ...formData, twitter_url: e.target.value })}
                  placeholder="https://x.com/username"
                />
              </div>

              {/* Active toggle */}
              <div className="flex items-center gap-3">
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={e => setFormData({ ...formData, is_active: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-10 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-teal-600"></div>
                </label>
                <span className="text-sm text-gray-700">有効</span>
              </div>
            </div>

            <div className="border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap"
              >
                キャンセル
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50 cursor-pointer whitespace-nowrap"
              >
                {saving ? '保存中...' : editingAuthor ? '更新' : '作成'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg p-6 shadow-xl max-w-sm w-full">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center bg-red-100 rounded-full">
                <i className="ri-delete-bin-line text-red-600 text-xl"></i>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">著者を削除</h3>
                <p className="text-sm text-gray-500">この操作は取り消せません</p>
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 cursor-pointer whitespace-nowrap"
              >
                キャンセル
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 cursor-pointer whitespace-nowrap"
              >
                削除する
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
